import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import { EditorComponent, EditorModule } from '@tinymce/tinymce-angular';
import { FormTokensService } from '@nrwl/rx-service-forms';
import { Ng2ImgMaxModule, Ng2ImgMaxService } from 'ng2-img-max';

import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/visualchars';
import 'tinymce/plugins/link';
import 'tinymce/plugins/directionality';
import 'tinymce/plugins/table';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/image';

@Component({
  selector: 'rx-richedit',
  templateUrl: './rx-richedit.component.html',
  styleUrls: ['./rx-richedit.component.scss'],
  imports: [CommonModule, FormsModule, EditorModule, Ng2ImgMaxModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RxRicheditComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RxRicheditComponent),
      multi: true,
    },
  ],
})
export class RxRicheditComponent
  implements OnInit, OnDestroy, ControlValueAccessor, OnChanges, Validator
{
  @Input() value: any | string;
  @Input() includeToolBar = false;
  @Input() toolItems = '';
  @Input() placeholder = '';
  @Input() inputToolbarId = '';
  @Input() autofocus = false;
  @Output() valueChange = new EventEmitter<string>();
  @Output() onFocus = new EventEmitter();
  @Output() onBlur = new EventEmitter();
  public control: FormControl;
  public toolbarId;

  // TODO: MOCKUP: Promote to snippets datasource service
  public specialChars = [
    { text: 'miligrams', value: '{miligrams}' },
    { text: 'milliliters', value: '{milliliters}' },
    { text: 'micrones', value: '{micrones}' },
  ];

  public editorCommands: any;

  // TinyMCE configuration
  public init = {
    inline: true,
    placeholder: '',
    // auto_focus: true,
    // smart_paste: true,
    fixed_toolbar_container: '#richedit-toolbar',
    paste_data_images: false,
    paste_block_drop: false,
    skin: false,
    content_css: false,
    cleanup_on_startup: true,
    fix_list_elements: true,
    fix_nesting: true,
    fix_table_elements: true,
    paste_use_dialog: true,
    paste_auto_cleanup_on_paste: true,
    toolbar_persist: true,
    paste_preprocess: function (plugin, args) {
      args.content = args.content
        .replace(/<img.*?alt="(.*?)"[^\>]+>/g, '')
        .replace(/style=".*?"/g, '')
        .replace(/<\s*strong[^>]*>/g, '')
        .replace(/<\/strong>/g, '')
        .replace(/<\s*em[^>]*>/g, '')
        .replace(/<\/em>/g, '')
        .replace(/<\s*b[^>]*>/g, '')
        .replace(/<\/b>/g, '')
        .replace(/<\s*i[^>]*>/g, '')
        .replace(/<\/i>/g, '')
        .replace(/<\s*h1[^>]*>/g, '')
        .replace(/<\/h1>/g, '')
        .replace(/<\s*h2[^>]*>/g, '')
        .replace(/<\/h2>/g, '')
        .replace(/<\s*h3[^>]*>/g, '')
        .replace(/<\/h3>/g, '')
        .replace(/<\s*h4[^>]*>/g, '')
        .replace(/<\/h4>/g, '');
    },
    plugins: 'autolink visualblocks visualchars link directionality table advlist lists',
    toolbar:
      'menuDateButton| formatselect styleselect | blocks | bold italic underline strikethrough link hr  | image | alignleft aligncenter alignright alignjustify | casechange blockquote bullist numlist outdent indent | undo redo',
    file_picker_callback: (cb, value, meta) => {
      const input = document.createElement('input') as HTMLInputElement;
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.onchange = (event: any) => {
        const file: File = event.path[0].files[0];

        this.ng2ImgMaxService.resize([file], 400, 1000).subscribe((result) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader?.result?.toString().split(',')[1];
            // call the callback and populate the Title field with the file name
            cb('data:image/png;base64,' + base64, {
              title: file.name,
            });
          };
          reader.readAsDataURL(result);
        });
      };

      input.click();
    },
    // toolbar_mode: 'sliding',
    // contextmenu: false,
    fontsize_formats: '13px 16px 18px 24px 32px',
    spellchecker_languages: 'English=en',
    branding: false,
    menubar: false,
    statusbar: false,
    block_formats: 'Paragraph=p; Header 1=h1; Header 2=h2; Header 3=h3; Header 4=h4; Header 5=h5',
    style_formats: [
      {
        title: 'Header 1',
        block: 'h1',
        styles: {
          'font-size': '32px',
          'font-weight': 'normal',
          'line-height': '37px',
        },
      },
      {
        title: 'Header 2',
        block: 'h2',
        styles: {
          'font-size': '24px',
          'font-weight': 'normal',
          'line-height': '28px',
        },
      },
      {
        title: 'Header 3',
        block: 'h3',
        styles: {
          'font-size': '18px',
          'font-weight': 'normal',
          'line-height': '21px',
        },
      },
      {
        title: 'Header 4',
        block: 'h4',
        styles: {
          'font-size': '16px',
          'font-weight': 'normal',
          'line-height': '18px',
        },
      },
      {
        title: 'Header 5',
        block: 'h5',
        styles: {
          'font-size': '13px',
          'font-weight': 'normal',
          'line-height': '15px',
        },
      },
    ],
    init_instance_callback: function (editor) {
      // This will trick the editor into thinking it was focused
      // without actually focusing it (causing the toolbar to appear)
      editor.dispatch('focus');
    },
    setup: (ctrl) => {
      // TODO: REFACTOR: Find better way to setup TinyMCE default font name and size
      ctrl.on('init', (t) => {
        this.editorCommands = t.target.editorCommands;
        // t.target.editorCommands.execCommand(
        //     'fontName',
        //     true,
        //     'Roboto'
        // );
        // t.target.editorCommands.execCommand('fontSize', false, '13px');
      });

      ctrl.on('blur', function () {
        return false;
      });

      // Shortcuts configuration & processing
      ctrl.ui.registry.addAutocompleter('specialchars', {
        trigger: '#',
        minChars: 1,
        columns: 1,
        fetch: (pattern) => {
          const matchedChars = this.specialChars.filter(
            (char) => char.text.indexOf(pattern) !== -1,
          );

          return new Promise((resolve) => {
            const results = matchedChars.map((char: { text: string; value: string }) => {
              return {
                value: char.value,
                text: char.text,
              };
            });
            resolve(results);
          });
        },
        onAction: function (autocompleteApi, rng, value) {
          ctrl.selection.setRng(rng);
          // TODO: IMPROVE: Insert inline control for interaction with shortcut
          ctrl.insertContent(value);
          autocompleteApi.hide();
        },
      });

      /* Menu button that has a simple "insert date" menu item, and a submenu containing other formats. */
      /* Clicking the first menu item or one of the submenu items inserts the date in the selected format. */
      ctrl.ui.registry.addMenuButton('menuDateButton', {
        text: 'Token',
        fetch: (callback) => {
          const items = this.tokensService.tokens
            .filter((item) => item.required)
            .map((item) => {
              return {
                type: 'menuitem',
                text: item.title,
                onAction: function (_) {
                  ctrl.insertContent(item.name);
                },
              };
            });
          callback(items);
        },
      });
    },
  };

  @ViewChild('richedit', { static: true }) richedit: EditorComponent;

  constructor(
    private ng2ImgMaxService: Ng2ImgMaxService,
    private tokensService: FormTokensService,
  ) {}

  ngOnInit(): void {
    if (this.includeToolBar) {
      this.toolbarId = `richedit-toolbar-${Math.floor(Math.random() * 9000 + 2)}`;
      this.init = {
        ...this.init,
        fixed_toolbar_container: `#${this.toolbarId}`,
      };
    }

    if (this.toolItems) {
      switch (this.toolItems) {
        case 'faq-settings': {
          const tools = 'bullist numlist | undo redo';
          this.init = { ...this.init, toolbar: tools };
          return;
        }
      }
    }

    if (this.inputToolbarId) {
      this.init = {
        ...this.init,
        fixed_toolbar_container: `#${this.inputToolbarId}`,
      };
    }

    if (this.placeholder) {
      this.init = { ...this.init, placeholder: this.placeholder };
    }
  }

  private destroying = false;
  private valueBeforeDestroy = '';

  ngOnDestroy(): void {
    this.suppressPendingChanges();
  }

  public suppressPendingChanges(): void {
    this.destroying = true;
    this.valueBeforeDestroy = this.value;
  }

  isValid(): boolean {
    return this.control && !this.control.pending && !this.control.errors && !this.control.invalid;
  }

  // validate
  validate(control: FormControl): ValidationErrors | any {
    this.control = control;
    return control.valid ? null : control.errors;
  }

  ngOnChanges(changes: SimpleChanges | any): void {
    // trigger validation
    this.onChange(this.value);
  }

  onModelChanged($event): void {
    this.onChange($event);
  }

  // input changes.
  onChange = (value: string): void => {};

  // touched/clicked.
  onTouched = (): void => {};

  // Register for change
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  // Register for touch/click
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Update the model and changes
  writeValue(value: string): void {
    if (this.value !== value) {
      this.value = value;
      this.onChange(this.value);
    }
  }

  onReset($event): void {
    this.value = null;
  }

  onValueChanged($event) {
    if (this.destroying && !$event && this.valueBeforeDestroy) {
      return;
    }
    this.onChange($event);
  }

  public execute(operation, token) {
    this.editorCommands.execCommand('mceInsertContent', false, token);

    // if(operation === 'insert'){
    //     this.richedit.writeValue('test');
    //     nativeElement.focus();

    //     setTimeout(() => {

    //         if (window.getSelection) {
    //             const sel = window.getSelection();
    //             if (sel.getRangeAt && sel.rangeCount) {
    //                 let range = sel.getRangeAt(0);
    //                 const tokenLength = token.length;

    //                 // Insert token into editable element at selection
    //                 const left = this.value.substring(0, range.startOffset);
    //                 const right = this.value.substring(range.endOffset, this.value.length);
    //                 const result = [left, token, right].join(' ');
    //                 this.value = result;
    //                 // Wait a tick to digest changes
    //                 setTimeout(() => {
    //                     // Setup start and end selection after insert
    //                     const selStart = result.indexOf(token, left.length) - 1;
    //                     const selEnd = selStart + tokenLength + 1;

    //                     // Clear selection
    //                     sel.removeAllRanges();

    //                     // Create new selection
    //                     range = document.createRange();

    //                     // Select editable element content
    //                     range.selectNodeContents(this.richedit.nativeElement);

    //                     // Set selection start and end
    //                     range.setStart(this.richedit.nativeElement.lastChild, selStart);
    //                     range.setEnd(this.richedit.nativeElement.lastChild, selEnd);

    //                     // Add selection range to editable element
    //                     sel.addRange(range);

    //                     // Focus editable element
    //                     this.richedit.nativeElement.focus();
    //                 })
    //             }
    //         }
    //     })
    // }
  }
}
